<template>
  <div class="top-nav">
    <!-- Hamburger Menu Button -->
    <button class="hamburger-btn" @click="toggleMenu" aria-label="Open menu">
      <i class="bi bi-list"></i>
    </button>

    <Logo class="nav-logo" />

    <div class="technician-name">
      <span v-if="loading">...</span>
      <span v-else>{{ technicianName }}</span>
    </div>

    <!-- Menu Overlay -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="menuOpen" class="menu-overlay" @click="closeMenu"></div>
      </Transition>
    </Teleport>

    <!-- Slide-out Menu -->
    <Teleport to="body">
      <Transition name="slide">
        <nav v-if="menuOpen" class="slide-menu">
          <div class="menu-header">
            <span class="menu-title">Menu</span>
            <button class="close-btn" @click="closeMenu" aria-label="Close menu">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <ul class="menu-list">
            <li>
              <router-link to="/timesheet" @click="closeMenu" class="menu-item">
                <i class="bi bi-calendar-week"></i>
                <span>Submit Timesheet</span>
              </router-link>
            </li>
            <li>
              <router-link to="/bookings" @click="closeMenu" class="menu-item">
                <i class="bi bi-calendar-check"></i>
                <span>My Bookings</span>
              </router-link>
            </li>
            <li>
              <router-link to="/reviews" @click="closeMenu" class="menu-item">
                <i class="bi bi-star"></i>
                <span>Reviews</span>
              </router-link>
            </li>
            <li>
              <router-link to="/inspections" @click="closeMenu" class="menu-item">
                <i class="bi bi-clipboard-check"></i>
                <span>TI Reports</span>
              </router-link>
            </li>
            <li>
              <router-link to="/commercial-reports" @click="closeMenu" class="menu-item">
                <i class="bi bi-file-earmark-medical"></i>
                <span>Commercial Reports</span>
              </router-link>
            </li>
            <li>
              <router-link to="/profile" @click="closeMenu" class="menu-item">
                <i class="bi bi-person"></i>
                <span>Profile</span>
              </router-link>
            </li>

            <li class="menu-divider"></li>

            <li>
              <button @click="handleLogout" class="menu-item logout-btn">
                <i class="bi bi-box-arrow-right"></i>
                <span>Logout</span>
              </button>
            </li>
          </ul>

          <!-- Technician Info at Bottom -->
          <div class="menu-footer">
            <div class="menu-user-info">
              <i class="bi bi-person-circle"></i>
              <span>{{ technicianName }}</span>
            </div>
          </div>
        </nav>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { createApiServiceWithAuth } from '@/services/api'
import Logo from '@/components/Logo.vue'

export default {
  name: 'TopNavigation',
  components: {
    Logo
  },
  setup() {
    const auth0 = useAuth0()
    const { user, logout } = auth0
    const apiService = createApiServiceWithAuth(auth0)

    const loading = ref(true)
    const technicianName = ref('Driver')
    const menuOpen = ref(false)

    const fetchTechnicianName = async () => {
      try {
        const email = user.value?.email
        if (!email) {
          console.warn('[TopNav] No email found in user profile')
          loading.value = false
          return
        }

        const data = await apiService.getTechnicianProfile(email)
        const profile = data.profile

        // Extract first name only for nav bar
        if (profile.first_name) {
          technicianName.value = profile.first_name.trim()
        } else if (profile.full_name) {
          technicianName.value = profile.full_name.trim().split(' ')[0]
        } else if (user.value?.name) {
          technicianName.value = user.value.name.split(' ')[0]
        }

      } catch (error) {
        console.error('[TopNav] Error fetching technician profile:', error)
        // Keep default "Driver" on error
      } finally {
        loading.value = false
      }
    }

    const toggleMenu = () => {
      menuOpen.value = !menuOpen.value
      // Prevent body scroll when menu is open
      document.body.style.overflow = menuOpen.value ? 'hidden' : ''
    }

    const closeMenu = () => {
      menuOpen.value = false
      document.body.style.overflow = ''
    }

    const handleLogout = () => {
      closeMenu()
      logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      })
    }

    // Close menu on escape key
    const handleKeydown = (e) => {
      if (e.key === 'Escape' && menuOpen.value) {
        closeMenu()
      }
    }

    onMounted(() => {
      fetchTechnicianName()
      document.addEventListener('keydown', handleKeydown)
    })

    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = ''
    })

    return {
      loading,
      technicianName,
      menuOpen,
      toggleMenu,
      closeMenu,
      handleLogout
    }
  }
}
</script>

<style scoped>
.top-nav {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background-color: var(--surface);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.hamburger-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  margin-right: 0.5rem;
}

.hamburger-btn:hover {
  background-color: var(--divider);
}

.hamburger-btn:active {
  transform: scale(0.95);
}

.nav-logo {
  height: 40px;
  flex: 1;
}

.technician-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  padding: 0.375rem 0.75rem;
  background-color: var(--primary-light);
  border-radius: 6px;
  text-align: center;
  white-space: nowrap;
}

.technician-name span {
  display: inline-block;
}

/* Menu Overlay */
.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  backdrop-filter: blur(2px);
}

/* Slide-out Menu */
.slide-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  max-width: 85vw;
  height: 100vh;
  background-color: var(--surface);
  z-index: 999;
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
}

.menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--divider);
}

.menu-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background-color: var(--divider);
  color: var(--text-primary);
}

/* Menu List */
.menu-list {
  list-style: none;
  padding: 0.5rem 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
}

.menu-list li {
  margin: 0;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1.25rem;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
}

.menu-item i {
  font-size: 1.25rem;
  color: var(--text-secondary);
  transition: color 0.2s ease;
}

.menu-item:hover {
  background-color: var(--primary-light);
}

.menu-item:hover i {
  color: var(--primary);
}

.menu-item.router-link-active {
  background-color: var(--primary-light);
  color: var(--primary);
}

.menu-item.router-link-active i {
  color: var(--primary);
}

.menu-divider {
  height: 1px;
  background-color: var(--divider);
  margin: 0.5rem 1.25rem;
}

.logout-btn {
  color: var(--error);
}

.logout-btn i {
  color: var(--error);
}

.logout-btn:hover {
  background-color: rgba(239, 68, 68, 0.1);
}

/* Menu Footer */
.menu-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--divider);
  background-color: var(--background);
}

.menu-user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.menu-user-info i {
  font-size: 1.5rem;
  color: var(--primary);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
